import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
    Box,
    CircularProgress,
    LinearProgress,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    IconButton,
    Paper,
    Stepper,
    Step,
    StepLabel,
    Chip,
    Drawer,
    Tooltip,
} from "@mui/material";
import { ArrowBack, ArrowForward, KeyboardArrowLeft, OpenInNew, ViewSidebar, ZoomIn, ZoomOut, RotateLeft, RotateRight, Layers, LayersOutlined } from "@mui/icons-material";
import { Document, Page } from "react-pdf";

import { AppDispatch, RootState } from "../../configureStore";
import { getReturnTaskByKey, getReturnPdfByRef, SET_HAS_CHANGES } from "../../redux/reducer/returns";
import { GroupTask, MatchFormsData, PageInfo } from "../../interface/returns";
import globalStyles from "../../globalStyles";
import PageGroupVerifier from "../organisms/PageGroupVerifier";
import FormEntityAssigner from "../organisms/FormEntityAssigner";
import usePdfZoom from "../../hooks/usePdfZoom";

const PDF_BASE_WIDTH = 700;


interface MatchFormsParams {
}

const STORAGE_KEY_PREFIX = 'match_forms_';
const STORAGE_EXPIRY_DAYS = 7;

interface LocalStorageData {
    matchData: MatchFormsData;
    currentPage: number;
    timestamp: number;
}

// Migrate old localStorage format (pages: number[]) to new format (pages: PageInfo[])
const migrateMatchData = (data: MatchFormsData): MatchFormsData => {
    const migratedPageGroups = { ...data.pageGroups };

    Object.keys(migratedPageGroups).forEach((groupId) => {
        const group = migratedPageGroups[groupId];
        if (group.pages && group.pages.length > 0) {
            // Check if pages is in old format (number[]) vs new format (PageInfo[])
            const firstPage = group.pages[0];
            if (typeof firstPage === 'number') {
                // Migrate from number[] to PageInfo[]
                migratedPageGroups[groupId] = {
                    ...group,
                    pages: (group.pages as unknown as number[]).map((page): PageInfo => ({ page })),
                };
            }
        }
    });

    return {
        ...data,
        pageGroups: migratedPageGroups,
    };
};

const STEP_LABELS = ['Verify Groups', 'Assign Forms'];

type LoadingPhase = 'task' | 'pdf' | null;

const MatchForms = ({
}: MatchFormsParams) => {
    const dispatch = useDispatch<AppDispatch>();
    const { loanId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const key = searchParams.get('key');

    const [loadingPhase, setLoadingPhase] = useState<LoadingPhase>('task');
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [numPages, setNumPages] = useState<number>(0);
    const [showBackDialog, setShowBackDialog] = useState(false);
    const [isFloating, setIsFloating] = useState(false);
    const {
        zoomPercent, zoomIn, zoomOut, reset: resetZoom, canZoomIn, canZoomOut, isDefault: isDefaultZoom,
    } = usePdfZoom('match');
    const [rotations, setRotations] = useState<Map<number, number>>(new Map());
    const [rotateAllPages, setRotateAllPages] = useState(false);
    const [matchData, setMatchData] = useState<MatchFormsData>({
        pageGroups: {},
        formAssignments: {},
        taxYearCorrections: {},
        currentStep: 1,
    });

    const groupTask = useSelector<RootState, GroupTask | undefined>(({ returns }) => {
        const activeTask = returns.activeReturnTask;
        return activeTask?.type === 'group' ? activeTask as GroupTask : undefined;
    });
    const hasChanges = useSelector<RootState, boolean>(({ returns }) => returns.hasChanges);
    const activePdf = useSelector<RootState, string | undefined>(({ returns }) => returns.activePdf);

    const clearHasChanges = () => dispatch({ type: SET_HAS_CHANGES, hasChanges: false });

    const getLocalStorageKey = (storageKey: string): string => `${STORAGE_KEY_PREFIX}${storageKey}`;

    const saveToLocalStorage = useCallback((storageKey: string, data: LocalStorageData): void => {
        try {
            localStorage.setItem(getLocalStorageKey(storageKey), JSON.stringify(data));
        } catch (error) {
            console.error('Failed to save to localStorage:', error);
        }
    }, []);

    const loadFromLocalStorage = useCallback((storageKey: string): LocalStorageData | null => {
        try {
            const stored = localStorage.getItem(getLocalStorageKey(storageKey));
            if (!stored) return null;

            const data: LocalStorageData = JSON.parse(stored);
            const now = Date.now();
            const ageInDays = (now - data.timestamp) / (1000 * 60 * 60 * 24);

            if (ageInDays > STORAGE_EXPIRY_DAYS) {
                console.warn(`localStorage data is ${ageInDays.toFixed(1)} days old, ignoring`);
                clearLocalStorage(storageKey);
                return null;
            }

            return data;
        } catch (error) {
            console.error('Failed to load from localStorage:', error);
            return null;
        }
    }, []);

    const clearLocalStorage = useCallback((storageKey: string): void => {
        try {
            localStorage.removeItem(getLocalStorageKey(storageKey));
        } catch (error) {
            console.error('Failed to clear localStorage:', error);
        }
    }, []);

    // Fetch task data
    useEffect(() => {
        if (!key) {
            navigate('/returns');
            return;
        }

        setLoadingPhase('task');
        dispatch(getReturnTaskByKey({
            input: { key },
            onSuccess: (response: { data: GroupTask }) => {
                const { data } = response;
                const savedData = loadFromLocalStorage(key);
                if (savedData) {
                    // Migrate old format if needed
                    setMatchData(migrateMatchData(savedData.matchData));
                    setCurrentPage(savedData.currentPage);
                }

                // Start PDF fetch
                if (data?.blobs?.pdfRef) {
                    setLoadingPhase('pdf');
                    setDownloadProgress(0);
                    dispatch(getReturnPdfByRef({
                        input: {
                            container: data.blobs.pdfRef.container,
                            path: data.blobs.pdfRef.path,
                        },
                        onProgress: (progress: number) => setDownloadProgress(progress),
                        onSuccess: () => {
                            setLoadingPhase(null);
                        },
                        onError: () => {
                            console.error('Failed to fetch PDF');
                            setLoadingPhase(null);
                        },
                    }));
                } else {
                    setLoadingPhase(null);
                }
            },
            onError: () => {
                console.error('Failed to fetch return task');
                navigate('/returns');
            },
        }));
    }, [key, dispatch, navigate, loadFromLocalStorage]);

    // Auto-save to localStorage
    useEffect(() => {
        if (key && matchData && Object.keys(matchData.pageGroups).length > 0) {
            saveToLocalStorage(key, {
                matchData,
                currentPage,
                timestamp: Date.now(),
            });
        }
    }, [matchData, currentPage, key, saveToLocalStorage]);

    // Handle step changes
    const handleNextStep = useCallback(() => {
        setMatchData({
            ...matchData,
            currentStep: 2,
        });
    }, [matchData, setMatchData]);

    const handlePrevStep = useCallback(() => {
        setMatchData({
            ...matchData,
            currentStep: 1,
        });
    }, [matchData, setMatchData]);

    const pdfUrl = activePdf ?? null;

    const handleDocumentLoadSuccess = ({ numPages: pages }: { numPages: number }) => {
        setNumPages(pages);
    };

    const handleOpenPdfInNewTab = useCallback(() => {
        if (pdfUrl) {
            window.open(pdfUrl, '_blank');
        }
    }, [pdfUrl]);

    const handlePageChange = useCallback((newPage: number) => {
        if (newPage >= 1 && newPage <= numPages) {
            setCurrentPage(newPage);
        }
    }, [numPages]);

    const applyRotation = useCallback((delta: number) => {
        setRotations((prev) => {
            const current = prev.get(currentPage) ?? 0;
            const next = (current + delta + 360) % 360;
            const updated = new Map(prev);
            if (rotateAllPages) {
                for (let p = 1; p <= numPages; p++) {
                    updated.set(p, next);
                }
            } else {
                updated.set(currentPage, next);
            }
            return updated;
        });
    }, [currentPage, numPages, rotateAllPages]);

    const handleRotateLeft = useCallback(() => {
        applyRotation(-90);
    }, [applyRotation]);

    const handleRotateRight = useCallback(() => {
        applyRotation(90);
    }, [applyRotation]);

    const toggleRotateAllPages = useCallback(() => {
        setRotateAllPages((prev) => !prev);
    }, []);

    const handleBack = useCallback(() => {
        if (hasChanges) {
            setShowBackDialog(true);
        } else {
            navigate('/returns');
        }
    }, [hasChanges, navigate]);

    const handleConfirmBack = () => {
        setShowBackDialog(false);
        clearHasChanges();
        if (key) {
            clearLocalStorage(key);
        }
        navigate('/returns');
    };

    const handleCancelBack = () => {
        setShowBackDialog(false);
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                return;
            }

            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                if (e.shiftKey) {
                    handleRotateLeft();
                } else {
                    handlePageChange(currentPage - 1);
                }
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                if (e.shiftKey) {
                    handleRotateRight();
                } else {
                    handlePageChange(currentPage + 1);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentPage, handlePageChange, handleRotateLeft, handleRotateRight]);

    // Compute info chips for header
    const totalFormCount = useMemo(
        () => Object.keys(matchData.pageGroups).length,
        [matchData.pageGroups],
    );
    const assignedFormCount = useMemo(
        () => Object.values(matchData.formAssignments).filter((a) => a.assignedTo).length,
        [matchData.formAssignments],
    );

    const loading = loadingPhase !== null || !groupTask || !pdfUrl;
    const activeStep = matchData.currentStep - 1; // 0-indexed for Stepper

    // Work panel content
    const workPanelContent = matchData.currentStep === 1 ? (
        <PageGroupVerifier
            groupTask={groupTask!}
            matchData={matchData}
            setMatchData={setMatchData}
            onPageChange={handlePageChange}
            onNext={handleNextStep}
        />
    ) : (
        <FormEntityAssigner
            groupTask={groupTask!}
            activeLoan={groupTask!.blobs?.loan}
            matchData={matchData}
            setMatchData={setMatchData}
            onPageChange={handlePageChange}
            onBack={handlePrevStep}
        />
    );

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {loading ? (
                <Box
                    sx={{
                        ...globalStyles.center,
                        flexDirection: "column",
                        gap: 3,
                    }}
                >
                    <Typography variant="h6" color="text.secondary">
                        {loadingPhase === 'task' ? 'Loading task data...' : 'Loading PDF...'}
                    </Typography>
                    {loadingPhase === 'pdf' && downloadProgress > 0 && (
                        <Box sx={{ width: '400px', textAlign: 'center' }}>
                            <LinearProgress
                                variant="determinate"
                                value={downloadProgress}
                                sx={{ mb: 1, height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                                {downloadProgress}% downloaded
                            </Typography>
                        </Box>
                    )}
                    {(loadingPhase === 'task' || downloadProgress === 0) && <CircularProgress />}
                </Box>
            ) : (
                <>
                    {/* Header */}
                    <Box
                        sx={{
                            px: 2,
                            py: 1,
                            borderBottom: 1,
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <IconButton onClick={handleBack} size="small">
                            <KeyboardArrowLeft />
                        </IconButton>

                        <Stepper
                            activeStep={activeStep}
                            sx={{
                                minWidth: 240,
                                '& .MuiStepLabel-label': { fontSize: '0.8rem' },
                            }}
                        >
                            {STEP_LABELS.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        <Box sx={{ flex: 1 }} />

                        {matchData.currentStep === 2 && (
                            <>
                                <Chip
                                    label={`${totalFormCount} forms`}
                                    size="small"
                                    variant="outlined"
                                />
                                <Chip
                                    label={`${assignedFormCount} assigned`}
                                    size="small"
                                    color={assignedFormCount === totalFormCount ? 'success' : 'default'}
                                    variant="outlined"
                                />
                            </>
                        )}

                        <Typography variant="body2" color="text.secondary">
                            {groupTask?.blobs?.loan?.loanNumber || loanId}
                        </Typography>

                        <Tooltip title={isFloating ? "Dock panel" : "Float panel over PDF"}>
                            <IconButton
                                size="small"
                                onClick={() => setIsFloating((prev) => !prev)}
                                color={isFloating ? 'primary' : 'default'}
                            >
                                <ViewSidebar />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Main content */}
                    <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
                        {/* PDF Viewer */}
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                borderRight: isFloating ? 0 : 1,
                                borderColor: 'divider',
                                overflow: 'auto',
                                minHeight: 0,
                            }}
                        >
                            {/* PDF Controls (top) */}
                            <Box
                                sx={{
                                    p: 1,
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    flexShrink: 0,
                                    position: 'sticky',
                                    top: 0,
                                    bgcolor: 'background.paper',
                                    zIndex: 1,
                                }}
                            >
                                <IconButton
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage <= 1}
                                    size="small"
                                >
                                    <ArrowBack />
                                </IconButton>
                                <Typography variant="body2">
                                    Page {currentPage} of {numPages}
                                </Typography>
                                <IconButton
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage >= numPages}
                                    size="small"
                                >
                                    <ArrowForward />
                                </IconButton>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
                                    <Tooltip title="Rotate left (Shift+←)">
                                        <IconButton onClick={handleRotateLeft} size="small">
                                            <RotateLeft />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Rotate right (Shift+→)">
                                        <IconButton onClick={handleRotateRight} size="small">
                                            <RotateRight />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={rotateAllPages ? "Rotation applies to all pages" : "Rotation applies to this page only"}>
                                        <IconButton
                                            onClick={toggleRotateAllPages}
                                            size="small"
                                            color={rotateAllPages ? "primary" : "default"}
                                        >
                                            {rotateAllPages ? <Layers /> : <LayersOutlined />}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Tooltip title="Zoom out">
                                        <span>
                                            <IconButton onClick={zoomOut} disabled={!canZoomOut} size="small">
                                                <ZoomOut />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Reset zoom">
                                        <span>
                                            <IconButton
                                                onClick={resetZoom}
                                                disabled={isDefaultZoom}
                                                size="small"
                                                sx={{ minWidth: 48 }}
                                            >
                                                <Typography variant="caption">{zoomPercent}%</Typography>
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Zoom in">
                                        <span>
                                            <IconButton onClick={zoomIn} disabled={!canZoomIn} size="small">
                                                <ZoomIn />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                                <IconButton onClick={handleOpenPdfInNewTab} title="Open in new tab" size="small">
                                    <OpenInNew />
                                </IconButton>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', p: 2, minHeight: 0 }}>
                                <Paper elevation={3}>
                                    <Document
                                        file={pdfUrl}
                                        onLoadSuccess={handleDocumentLoadSuccess}
                                        loading={<CircularProgress />}
                                    >
                                        <Page
                                            pageNumber={currentPage}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                            width={PDF_BASE_WIDTH * (zoomPercent / 100)}
                                            rotate={rotations.get(currentPage) ?? 0}
                                        />
                                    </Document>
                                </Paper>
                            </Box>

                            {/* PDF Controls (bottom) */}
                            <Box
                                sx={{
                                    p: 2,
                                    borderTop: 1,
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    flexShrink: 0,
                                }}
                            >
                                <IconButton
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage <= 1}
                                >
                                    <ArrowBack />
                                </IconButton>
                                <Typography>
                                    Page {currentPage} of {numPages}
                                </Typography>
                                <IconButton
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage >= numPages}
                                >
                                    <ArrowForward />
                                </IconButton>
                                <IconButton onClick={handleOpenPdfInNewTab} title="Open in new tab">
                                    <OpenInNew />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Step-based content — inline or Drawer */}
                        {isFloating ? (
                            <Drawer
                                variant="persistent"
                                anchor="right"
                                open
                                PaperProps={{
                                    sx: {
                                        width: '45%',
                                        minWidth: 420,
                                        maxWidth: 700,
                                        top: 'auto',
                                        height: 'calc(100% - 49px)',
                                        mt: '49px',
                                    },
                                }}
                            >
                                {workPanelContent}
                            </Drawer>
                        ) : (
                            <Box
                                sx={{
                                    width: '45%',
                                    minWidth: 400,
                                    maxWidth: 650,
                                    flexShrink: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'auto',
                                    minHeight: 0,
                                }}
                            >
                                {workPanelContent}
                            </Box>
                        )}
                    </Box>

                    {/* Back confirmation dialog */}
                    <Dialog open={showBackDialog} onClose={handleCancelBack}>
                        <DialogTitle>Unsaved Changes</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                You have unsaved changes. Are you sure you want to leave and lose all changes?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCancelBack} color="primary">
                                Cancel
                            </Button>
                            <Button onClick={handleConfirmBack} color="error" autoFocus>
                                Leave and Discard Changes
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </Box>
    );
};

export default MatchForms;
